import {Component, Signal, computed} from '@angular/core';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {toSignal, toObservable} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, timer} from 'rxjs';
import {VtkService} from '../../../services/vtk.service';
import {VtkJobDetail, VtkJobMetrics, VTK_STATUS_LABELS, VTK_STATUS_COLORS} from '../../../services/vtk.types';
import {ContextService} from '../../../services/context.service';
import {Organization} from '../../../types-repository';
import {Airline} from '../../../types-workflow';
import {LogoAreaComponent, BreadCrumbs} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {CldatePipe} from '../../../pipes/cldate.pipe';
import {DurationPipe} from '../../../pipes/duration.pipe';
import {FileSizePipe} from '../../../pipes/file-size-pipe';

@Component({
  selector: 'app-vtk-job-detail',
  template: `
    <app-logo-area [breadcrumbs]="breadCrumbs()"></app-logo-area>
    <main>
    <app-headline-section [title]="'VTK Job ' + jobId()"></app-headline-section>
    <section class="cl-section cl-section-regular">
    <div class="container"><div class="row"><div class="col-12">

    @if (job(); as job) {
      <div class="card">
        <div class="card-body">
          <dl class="cl-dl">
            <dt>Status</dt>
            <dd><span class="cl-badge cl-badge-sm" [class]="statusColor(job.status)">{{ statusLabel(job.status) }}</span></dd>
            <dt>User</dt><dd>{{ job.user || '-' }}</dd>
            <dt>Region</dt><dd>{{ job.region || '-' }}</dd>
            <dt>Priority</dt><dd>{{ job.priority }}</dd>
            <dt>Bytes in</dt><dd>{{ job.bytes_in | fileSize }}</dd>
            <dt>Bytes out</dt><dd>{{ job.bytes_out | fileSize }}</dd>
            <dt>Execution time</dt><dd>{{ job.execution_time | duration }}</dd>
            @if (metrics(); as m) {
              <dt>Compute Units</dt><dd>{{ m.cus.toFixed(2) }}</dd>
              @if (m.video_minutes_in) { <dt>Video minutes in</dt><dd>{{ m.video_minutes_in }}</dd> }
              @if (m.video_minutes_out) { <dt>Video minutes out</dt><dd>{{ m.video_minutes_out }}</dd> }
              @if (m.gb_in) { <dt>GB in</dt><dd>{{ m.gb_in.toFixed(3) }}</dd> }
              @if (m.gb_out) { <dt>GB out</dt><dd>{{ m.gb_out.toFixed(3) }}</dd> }
            }
            <dt>Created</dt><dd>{{ job.created | cldate }}</dd>
            <dt>Updated</dt><dd>{{ job.modified | cldate }}</dd>
          </dl>
        </div>
      </div>

      @if (job.tags?.length) {
        <p class="cl-spacing-box-micro">
          @for (tag of job.tags; track tag) {
            <span class="cl-badge cl-badge-sm cl-color-clay">{{ tag }}</span>
          }
        </p>
      }

      <h3 class="cl-spacing-medium">Tasks ({{ job.tasks?.length ?? 0 }})</h3>
      <table class="cl-table-data" style="width: 100%">
        <thead>
          <tr>
            <th class="cl-table-head cl-p-tiny">Tool</th>
            <th class="cl-table-head cl-p-tiny">Version</th>
            <th class="cl-table-head cl-p-tiny">Status</th>
          </tr>
        </thead>
        <tbody>
          @for (task of job.tasks; track task.id) {
            <tr style="cursor: pointer" [routerLink]="['tasks', task.id]" [queryParams]="{org_urn: orgUrn()}">
              <td>{{ task.tool }}</td>
              <td>{{ task.version }}</td>
              <td><span class="cl-badge cl-badge-sm" [class]="statusColor(task.status)">{{ statusLabel(task.status) }}</span></td>
            </tr>
          } @empty {
            <tr><td colspan="3">No tasks.</td></tr>
          }
        </tbody>
      </table>
    }

    </div></div></div>
    </section>
    </main>
  `,
  styles: [`
    :host {
      height: calc(100% - 45px);
      display: table;
    }
  `],
  imports: [RouterLink, LogoAreaComponent, HeadlineSectionComponent, CldatePipe, DurationPipe, FileSizePipe]
})
export class VtkJobDetailComponent {
  organization: Signal<Organization>;
  airline: Signal<Airline>;
  poName: Signal<string>;
  poItemId: Signal<string>;
  jobId: Signal<string>;
  orgUrn: Signal<string>;
  job: Signal<VtkJobDetail | undefined>;
  metrics: Signal<VtkJobMetrics | undefined>;
  breadCrumbs: Signal<BreadCrumbs>;

  constructor(
    private route: ActivatedRoute,
    private vtkService: VtkService,
    private contextService: ContextService,
  ) {
    this.organization = this.contextService.organization;
    this.airline = this.contextService.airline;
    this.poName = toSignal(this.route.params.pipe(map(p => p['po']), distinctUntilChanged()), {initialValue: ''});
    this.poItemId = toSignal(this.route.params.pipe(map(p => p['poitem']), distinctUntilChanged()), {initialValue: ''});
    this.jobId = toSignal(this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged()), {initialValue: ''});
    this.orgUrn = toSignal(this.route.queryParams.pipe(map(p => p['org_urn']), distinctUntilChanged()), {initialValue: ''});

    const orgUrn$ = this.route.queryParams.pipe(map(p => p['org_urn'] as string), filter(Boolean), distinctUntilChanged());
    const jobId$ = this.route.params.pipe(map(p => p['job_id']), distinctUntilChanged());
    const poName$ = this.route.params.pipe(map(p => p['po']), distinctUntilChanged());

    this.job = toSignal(
      combineLatest([orgUrn$, poName$, jobId$]).pipe(
        switchMap(([orgUrn, poName, jobId]) =>
          timer(0, 10000).pipe(
            switchMap(() => this.vtkService.getJob(orgUrn, poName, jobId).pipe(catchError(() => of(undefined))))
          )
        )
      )
    );

    this.metrics = toSignal(
      combineLatest([orgUrn$, poName$, jobId$]).pipe(
        switchMap(([orgUrn, poName, jobId]) =>
          this.vtkService.getJobMetrics(orgUrn, poName, jobId).pipe(catchError(() => of(undefined)))
        )
      )
    );

    this.breadCrumbs = computed(() => {
      const org_id = this.organization()?.id;
      const airline = this.airline();
      const po = this.poName();
      const poItemId = this.poItemId();
      if (!org_id || !airline) return [];
      return [
        {display: 'Content Platform', commands: ['/o', org_id]},
        {display: 'Workflows', commands: ['/o', org_id, 'workflows']},
        {display: airline.airline_name, commands: ['/o', org_id, 'workflows', airline.iata_code]},
        {display: po, commands: ['/o', org_id, 'workflows', airline.iata_code, po]},
        {display: poItemId, commands: ['/o', org_id, 'workflows', airline.iata_code, po, poItemId]},
      ];
    });
  }

  statusLabel(status: number): string { return VTK_STATUS_LABELS[status] ?? 'Unknown'; }
  statusColor(status: number): string { return VTK_STATUS_COLORS[status] ?? 'cl-color-ash'; }
}
